import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Icon } from 'ui';
import moment from 'utils/cops-moment';
import { withRouter } from 'utils/withRouter';
import usePermissionCheck from '../../../../../utils/hooks/usePermissionCheck';
import { blockConfig, laneConfig } from '../../../config';
import { dayViews, viewIds } from '../../../config/view';
import Unavailability from '../../unavailability/containers/unavailability-container';
import Dropzone from '../containers/lane-dropzone-container';

const LaneWrapper = styled.div`
  position: relative;
`;

const StyledSpan = styled.span`
width: 100%;
overflow: hidden;
white-space: nowrap;
  text-overflow: ellipsis;
`;

const GridWrapper = styled.div`
  min-height: ${p =>
    // eslint-disable-next-line
    (p.totalLines || 1) * blockConfig.height + laneConfig.minHeight}px;
  display: grid;
  grid-template-columns: repeat(
    ${p => p.amountOfDays ? p.amountOfDays : 0},
    ${p => p.widthDay ? p.widthDay : 0}%
  );
  grid-gap: ${p => p.widthDayBreaker}px;
`;

const DayWrapper = styled.div`
  border-bottom: 1px solid #bac8d5;
  border-left: 1px solid #bac8d5;
  border-right: 1px solid #bac8d5;
  background-color: #ecf2f6;
  overflow: hidden;
`;

const Menu = styled.div`
  top: 5px;;
  border-radius: 3px;
  background-color: white;
  position: absolute;
  cursor: pointer;
  width: 200px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  box-shadow: 3px 3px 3px #bac8d5;
`;

const StyledIcon = styled(Icon)`
  color: ${p => p.theme.colors.text} !important;
  margin: 0 5px;
`;

const MenuItem = styled.p`
  color: ${p => p.theme.colors.text} !important;
  border-radius: 3px;
  padding: 0 5px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 14px;
  &:hover {
    background-color: #e6f9ff;
  }
`;

/**
 *  Lane component
 *
 * @param {data} object - data of day for user
 * @param {ganttDimensions} object - Dimensions specific for gantt
 * @param {viewDimensions} object - Dimensions specific for view
 */
const Lane = ({
  data,
  ganttDimensions,
  viewDimensions,
  setLaneInfo,
  selectedIds,
  history,
  dateReference,
  view,
  multiStatusInfo,
}) => {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [totalLines, setTotalLines] = useState(1);
  const { unavailability, blocks, id } = data;
  const { widthDay } = ganttDimensions;
  const [selectedDate, setSelectedDate] = useState();
  const [checkPermission] = usePermissionCheck();
  const { amountOfDays, weeks } = viewDimensions;

  useEffect(() => {
    if (totalLines) {
      // set the totalLines for the linked laneInfo
      setLaneInfo({ id, totalLines });
    }
  }, [totalLines]);

  const openMenu = (date) => {
    setSelectedDate(date);
  };

  const renderLane = () => {
    if (id) {
      return (
        // eslint-disable-next-line no-undefined
        <LaneWrapper onMouseLeave={() => setSelectedDate(undefined)} data-test-id='gantt-lane'>
          {/** layer dropzone */}
          <Dropzone
            id={id}
            blocks={blocks}
            setTotalLines={setTotalLines}
            setIsDraggingOver={setIsDraggingOver}
            selectedIds={selectedIds}
            renderMenu={openMenu}
            multiStatusInfo={multiStatusInfo}
          />

          {/** layer unavailabilities */}
          <GridWrapper
            widthDay={widthDay}
            amountOfDays={amountOfDays}
            totalLines={totalLines}
            widthDayBreaker={view?.parameters?.widthDayBreaker}
          >
            {weeks?.map((week, index) => week?.map((dayNumber, i) => {
              let date = moment();
              if (view?.id === viewIds.month) {
                date = moment()
                  .year(dateReference.year)
                  .month(dateReference.month)
                  .startOf('month')
                  .add(i + (index * 7), 'days');
              } else if (dayViews.includes(view?.id)) {
                date = moment()
                  .year(dateReference.year)
                  .month(dateReference.month)
                  .date(dateReference.day)
                  .add(i, 'day');
              } else {
                date = moment()
                  .year(dateReference.year)
                  .month(dateReference.month)
                  .date(dateReference.day)
                  .startOf('week')
                  .add(i + (index * 7), 'day');
              }

              return <DayWrapper key={dayNumber} data-test-id='gantt-lane-day'>
                {selectedDate &&
                  (moment(selectedDate).format('DD-MM-YYYY') === date.format('DD-MM-YYYY')) &&
                  checkPermission('planboardmanage') &&
                  <Menu
                    // eslint-disable-next-line no-undefined
                    onMouseLeave={() => setSelectedDate(undefined)}
                  >
                    <MenuItem
                      onClick={() =>
                        history.push(
                          `new/absence/${id}/${moment(selectedDate).format('YYYYMMDD')}`,
                        )
                      }
                      data-test-id='gantt-add-time-off-btn'
                    >
                      <StyledIcon type='clock' size='sm' light={true} />
                      <StyledSpan>
                        <FormattedMessage id='planningBoard.blocks.ADD_TIME_OFF' />
                      </StyledSpan>
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        history.push(
                          `new/reservation/${id}/${moment(selectedDate).format('YYYYMMDD')}`,
                        )
                      }
                      data-test-id='gantt-add-reservation-btn'
                    >
                      <StyledIcon type='business-time' size='sm' />
                      <StyledSpan>
                        <FormattedMessage id='planningBoard.blocks.ADD_RESERVATION' />
                      </StyledSpan>
                    </MenuItem>
                  </Menu>
                }
                <Unavailability
                  unavailability={unavailability}
                  day={date.format('YYYYMMDD')}
                  isDraggingOver={isDraggingOver}
                />
              </DayWrapper>;
            }))}
          </GridWrapper>
        </LaneWrapper>
      );
    }
    return null;
  };

  return renderLane();
};

Lane.propTypes = {
  data: PropTypes.object,
  ganttDimensions: PropTypes.object,
  viewDimensions: PropTypes.object,
  dateReference: PropTypes.object,
  setLaneInfo: PropTypes.func,
  selectedIds: PropTypes.array,
  multiStatusInfo: PropTypes.object,
};

export default withRouter(Lane);
